import { useQueryClient } from '@tanstack/react-query'
import { EyeIcon, XIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useUser } from '@/api/user/queries/useUser'
import { Button } from '@/components/ui/button'
import { clearImpersonation, useImpersonatedUserId } from '@/lib/impersonation'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { cn } from '@/lib/utils'
import { OfficeRoutePath } from '@/RoutePath'

type Props = {
  className?: string
}

export function ImpersonationBanner({ className }: Props) {
  const impersonatedUserId = useImpersonatedUserId()
  const { data: targetUser } = useUser(impersonatedUserId ?? '')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  if (!impersonatedUserId) {
    return null
  }

  const displayName =
    targetUser?.name && targetUser?.familyName ? `${targetUser.name} ${targetUser.familyName}` : (targetUser?.email ?? impersonatedUserId)

  const handleExit = async () => {
    const userId = impersonatedUserId
    clearImpersonation()
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LOGGED_USER] })
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })
    navigate(`${OfficeRoutePath.UserPage}/${userId}`)
  }

  return (
    <div
      className={cn(
        'border-border-default flex flex-wrap items-center justify-between gap-3 border-b bg-amber-50 px-4 py-2 text-sm text-amber-950',
        className
      )}
    >
      <div className='flex items-center gap-2'>
        <EyeIcon className='size-4 shrink-0' />
        <span>
          Viewing as <strong>{displayName}</strong>
        </span>
      </div>
      <Button type='button' variant='outline' size='sm' onClick={handleExit}>
        <XIcon className='size-4' />
        Exit impersonation
      </Button>
    </div>
  )
}
